import { needsBody, partsHaveVars, tryParseFormData } from './requestParts';

// ─── cURL ──────────────────────────────────────────────────────────────────

export const DEFAULT_CURL_OPTIONS = {
  multiline:       true,
  shortFlags:      false,
  quoteStyle:      'single',
  followRedirects: false,
  silent:          false,
  timeout:         0,
};

export function generateCurl(parts, options = {}) {
  const {
    multiline       = DEFAULT_CURL_OPTIONS.multiline,
    shortFlags      = DEFAULT_CURL_OPTIONS.shortFlags,
    quoteStyle      = DEFAULT_CURL_OPTIONS.quoteStyle,
    followRedirects = DEFAULT_CURL_OPTIONS.followRedirects,
    silent          = DEFAULT_CURL_OPTIONS.silent,
    timeout         = DEFAULT_CURL_OPTIONS.timeout,
  } = options;

  const q = quoteStyle === 'double' ? '"' : "'";
  const escapeQ = quoteStyle === 'double'
    ? (s) => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
    : (s) => s.replace(/'/g, "'\\''");

  const flagRequest = shortFlags ? '-X'        : '--request';
  const flagUrl     = shortFlags ? ''           : '--url ';
  const flagHeader  = shortFlags ? '-H'         : '--header';
  const flagData    = shortFlags ? '-d'         : '--data';
  const flagForm    = shortFlags ? '-F'         : '--form';

  const args = [];

  if (silent)          args.push(shortFlags ? '-s' : '--silent');
  if (followRedirects) args.push(shortFlags ? '-L' : '--location');
  if (timeout > 0)     args.push(`--max-time ${timeout}`);
  if (parts.method !== 'GET') args.push(`${flagRequest} ${parts.method}`);
  args.push(`${flagUrl}${q}${parts.url}${q}`);

  for (const [k, v] of Object.entries(parts.headers)) {
    args.push(`${flagHeader} ${q}${k}: ${escapeQ(v)}${q}`);
  }

  if (needsBody(parts.method, parts.body)) {
    if (parts.body_type === 'formdata') {
      const fields = tryParseFormData(parts.body);
      if (fields) {
        for (const f of fields) {
          args.push(`${flagForm} ${q}${f.key}=${escapeQ(f.value || '')}${q}`);
        }
      } else {
        args.push(`${flagData} ${q}${escapeQ(parts.body)}${q}`);
      }
    } else {
      args.push(`${flagData} ${q}${escapeQ(parts.body)}${q}`);
    }
  }

  const lines = [];
  if (partsHaveVars(parts)) lines.push('# Replace {{...}} placeholders with actual values\n');

  if (multiline) {
    lines.push(`curl ${args.join(' \\\n  ')}`);
  } else {
    lines.push(`curl ${args.join(' ')}`);
  }

  return lines.join('\n');
}

// ─── Python ────────────────────────────────────────────────────────────────

export const DEFAULT_PYTHON_OPTIONS = {
  timeout:         0,
  verifySsl:       true,
  followRedirects: true,
  responseFormat:  'text',
};

export function generatePython(parts, options = {}) {
  const {
    timeout         = DEFAULT_PYTHON_OPTIONS.timeout,
    verifySsl       = DEFAULT_PYTHON_OPTIONS.verifySsl,
    followRedirects = DEFAULT_PYTHON_OPTIONS.followRedirects,
    responseFormat  = DEFAULT_PYTHON_OPTIONS.responseFormat,
  } = options;

  const lines = [];
  if (partsHaveVars(parts)) lines.push('# Replace {{...}} placeholders with actual values\n');

  lines.push('import requests\n');
  lines.push(`url = "${parts.url}"\n`);

  const headerEntries = Object.entries(parts.headers);
  if (headerEntries.length) {
    lines.push('headers = {');
    for (const [k, v] of headerEntries) {
      lines.push(`    "${k}": "${v.replace(/"/g, '\\"')}",`);
    }
    lines.push('}\n');
  }

  const withBody = needsBody(parts.method, parts.body);
  if (withBody) {
    if (parts.body_type === 'formdata') {
      const fields = tryParseFormData(parts.body);
      if (fields) {
        lines.push('payload = {');
        for (const f of fields) {
          lines.push(`    "${f.key}": "${(f.value || '').replace(/"/g, '\\"')}",`);
        }
        lines.push('}\n');
      } else {
        lines.push(`payload = "${parts.body.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"\n`);
      }
    } else {
      lines.push(`payload = "${parts.body.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"\n`);
    }
  }

  const method = parts.method.toLowerCase();
  const headerArg = headerEntries.length ? ', headers=headers' : '';
  let bodyArg = '';
  if (withBody) {
    bodyArg = parts.body_type === 'formdata' && tryParseFormData(parts.body) ? ', data=payload' : ', data=payload';
  }
  const extraArgs = [
    ...(timeout > 0        ? [`timeout=${timeout}`]          : []),
    ...(!verifySsl         ? ['verify=False']                 : []),
    ...(!followRedirects   ? ['allow_redirects=False']        : []),
  ].map(a => `, ${a}`).join('');

  lines.push(`response = requests.${method}(url${headerArg}${bodyArg}${extraArgs})\n`);
  lines.push('print(response.status_code)');
  lines.push(responseFormat === 'json' ? 'print(response.json())' : 'print(response.text)');

  return lines.join('\n');
}

// ─── TypeScript ────────────────────────────────────────────────────────────

export const DEFAULT_TS_OPTIONS = {
  responseFormat: 'json',
  credentials:    'omit',
};

export function generateTypeScript(parts, options = {}) {
  const {
    responseFormat = DEFAULT_TS_OPTIONS.responseFormat,
    credentials    = DEFAULT_TS_OPTIONS.credentials,
  } = options;

  const lines = [];
  if (partsHaveVars(parts)) lines.push('// Replace {{...}} placeholders with actual values\n');

  const headerEntries = Object.entries(parts.headers);
  const withBody = needsBody(parts.method, parts.body);

  lines.push('const response = await fetch(');
  lines.push(`  "${parts.url}",`);
  lines.push('  {');
  lines.push(`    method: "${parts.method}",`);

  if (credentials !== 'omit') {
    lines.push(`    credentials: "${credentials}",`);
  }

  if (headerEntries.length) {
    lines.push('    headers: {');
    for (const [k, v] of headerEntries) {
      lines.push(`      "${k}": "${v.replace(/"/g, '\\"')}",`);
    }
    lines.push('    },');
  }

  if (withBody) {
    if (parts.body_type === 'formdata') {
      const fields = tryParseFormData(parts.body);
      if (fields) {
        lines.push('    body: (() => {');
        lines.push('      const form = new FormData();');
        for (const f of fields) {
          lines.push(`      form.append("${f.key}", "${(f.value || '').replace(/"/g, '\\"')}");`);
        }
        lines.push('      return form;');
        lines.push('    })(),');
      } else {
        lines.push(`    body: \`${parts.body.replace(/`/g, '\\`')}\`,`);
      }
    } else {
      lines.push(`    body: \`${parts.body.replace(/`/g, '\\`')}\`,`);
    }
  }

  lines.push('  }');
  lines.push(');\n');

  if (responseFormat === 'json') {
    lines.push('const data = await response.json();');
    lines.push('console.log(response.status, data);');
  } else if (responseFormat === 'text') {
    lines.push('const text = await response.text();');
    lines.push('console.log(response.status, text);');
  } else {
    lines.push('console.log(response.status);');
  }

  return lines.join('\n');
}
